import React, { useEffect, useRef, useState } from 'react'
import { NavLinkProps } from 'react-router-dom'
import ResizeObserver from 'resize-observer-polyfill'

import {
  NavigationLink,
  UnlinkedNavigationItem,
  ItemLabel,
  ProjectIcon,
  CollectionIcon,
  DocumentIcon,
  PageIcon,
  FrameIcon,
  PrototypeIcon,
  PrototypeStartPointIcon,
  ComponentsIcon,
  TemplateIcon,
  ItemTooltip,
  SymbolIcon,
  SectionIcon,
  VariantSetIcon,
  ChevronsIcon,
  ArchivePill,
} from './NavigationItem.styles'
import {
  BaseResponsiveDropdownHookProps,
  ButtonUnstyled,
  useResponsiveDropdown,
} from '@sketch/components'

const DUMMY_COMPONENT = () => null

export const icons = {
  project: ProjectIcon,
  collection: CollectionIcon,
  document: DocumentIcon,
  template: TemplateIcon,
  page: PageIcon,
  frame: FrameIcon,
  components: ComponentsIcon,
  prototype: PrototypeIcon,
  prototypeStartPoint: PrototypeStartPointIcon,
  layer: PrototypeIcon,
  symbol: SymbolIcon,
  section: SectionIcon,
  variantSet: VariantSetIcon,
}

interface NavigationItemProps<T extends React.ComponentType<any>> {
  icon?: keyof typeof icons
  label: React.ReactNode
  to?: NavLinkProps['to']
  collapsed?: boolean
  'data-testid'?: string
  secondary?: boolean
  dropdownComponent?: T
  dropdownComponentProps?: React.ComponentPropsWithoutRef<T>
  dropdownProps?: BaseResponsiveDropdownHookProps
  hideDropdownChevron?: boolean
  archived?: boolean
}

const NavigationItem = <T extends React.ComponentType<any>>(
  props: NavigationItemProps<T>
) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const labelRef = useRef<HTMLSpanElement>(null)

  const {
    secondary,
    dropdownComponent,
    dropdownComponentProps,
    dropdownProps,
    hideDropdownChevron,
    'data-testid': dataTestId = 'navigation-item',
    archived,
  } = props

  const [dropdownContent, buttonProps] = useResponsiveDropdown({
    dropdown: dropdownComponent || DUMMY_COMPONENT,
    dropdownProps: dropdownComponentProps,
    ...dropdownProps,
  })

  useEffect(() => {
    setShowTooltip(!!props.collapsed)

    if (!labelRef.current) {
      return
    }

    const checkOverflow = () => {
      const ref = labelRef.current
      const hasOverflow = (ref?.offsetWidth ?? 0) < (ref?.scrollWidth ?? 0)
      setShowTooltip(!!props.collapsed || hasOverflow)
    }

    const observer = new ResizeObserver(checkOverflow)
    observer.observe(labelRef.current)

    return () => observer.disconnect()
  }, [props.collapsed])

  const Icon = props.icon ? icons[props.icon] : null

  const content = (
    <>
      {Icon && <Icon />}
      {!props.collapsed && (
        <ItemLabel noIcon={Icon === null} ref={labelRef}>
          {props.label}
        </ItemLabel>
      )}
      {archived && <ArchivePill />}
      {showTooltip && (
        <ItemTooltip
          content={props.label}
          placement="bottom"
          disableWhenTouchDevice
        >
          <></>
        </ItemTooltip>
      )}
    </>
  )

  const isSymbol = props.icon === 'symbol'

  if (props.to) {
    return (
      <NavigationLink
        to={props.to}
        data-testid={dataTestId}
        $secondary={secondary}
        $isSymbol={isSymbol}
      >
        {content}
      </NavigationLink>
    )
  } else if (dropdownComponent) {
    return (
      <>
        <UnlinkedNavigationItem
          data-testid={dataTestId}
          $secondary={secondary}
          $isSymbol={isSymbol}
          as={ButtonUnstyled}
          {...buttonProps}
        >
          {content}
          {!hideDropdownChevron && <ChevronsIcon $isSymbol={isSymbol} />}
        </UnlinkedNavigationItem>
        {dropdownContent}
      </>
    )
  } else {
    return (
      <UnlinkedNavigationItem
        data-testid={dataTestId}
        $secondary={secondary}
        $isSymbol={isSymbol}
        {...buttonProps}
      >
        {content}
      </UnlinkedNavigationItem>
    )
  }
}

export default NavigationItem
